import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function CategorySection() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (name: string, url: string) => {
    setSelected(name);
    setIsOpen(false);
    window.open(url, '_blank', 'noopener,nofollow');
  };

  const iconMap: Record<string, string> = {
    'cat-shoes': 'ri-footprint-line',
    'cat-hoodies': 'ri-t-shirt-line',
    'cat-tshirts': 'ri-shirt-line',
    'cat-jackets': 'ri-t-shirt-2-line',
    'cat-pants': 'ri-user-3-line',
    'cat-headwear': 'ri-customer-service-line',
    'cat-sets': 'ri-stack-line',
    'cat-underwear': 'ri-user-line',
    'cat-jersey': 'ri-trophy-line',
    'cat-accessories': 'ri-glasses-line',
    'cat-bags': 'ri-shopping-bag-3-line',
    'cat-belts': 'ri-links-line',
    'cat-perfumes': 'ri-water-flash-line',
    'cat-other': 'ri-more-line',
  };

  return (
    <section id="categories" className="py-14 md:py-20 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-12">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
            {t('categories.title')}
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
            {t('categories.subtitle')}
          </p>
        </div>

        <div className="flex justify-center mb-10 md:mb-12">
          <div ref={dropdownRef} className="relative w-full max-w-md">
            <label className="block text-sm font-medium text-foreground-700 mb-2">
              {t('categories.dropdownLabel')}
            </label>
            <button
              className="w-full flex items-center justify-between px-4 py-3 text-sm bg-background-50 border border-background-300/60 rounded-md hover:border-primary-300 transition-colors cursor-pointer text-left"
              onClick={() => setIsOpen(!isOpen)}
            >
              <span className={selected ? 'text-foreground-800' : 'text-foreground-400'}>
                {selected || t('categories.dropdownPlaceholder')}
              </span>
              <i className={`text-foreground-500 transition-transform ${isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
            </button>

            {isOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-background-50 border border-background-200/70 rounded-md shadow-sm z-20 max-h-64 overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.seq}
                    className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-colors cursor-pointer whitespace-nowrap"
                    onClick={() => handleSelect(cat.name, cat.url)}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
          {categories.map((cat) => (
            <a
              key={cat.seq}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col items-center gap-2 p-4 md:p-5 bg-background-50 border border-background-200/70 rounded-lg hover:border-accent-300 hover:bg-accent-50/50 transition-all duration-200 cursor-pointer no-underline"
            >
              <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-600 group-hover:bg-accent-100 group-hover:text-accent-600 transition-colors">
                <i className={`${iconMap[cat.seq] || 'ri-folder-line'} text-lg md:text-xl`}></i>
              </div>
              <span className="text-xs md:text-sm font-medium text-foreground-700 group-hover:text-foreground-900 text-center leading-tight whitespace-nowrap">
                {cat.name}
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}